import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { buildScheduleMapMarkers, getScheduleMapPosition } from '@/components/SchedulePage/ScheduleMap/mapMarkers';
import { isAllInspectorsSelected } from '@/components/SchedulePage/ScheduleMap/helpers/selection';
import ScheduleMapComponent from '@/components/SchedulePage/ScheduleMap/ScheduleMapComponent';
import { getInitials, getInspectorColor } from '@/components/SchedulePage/ScheduleMap/utils';
import { getGeocodableEntityId } from '@/services/geocodableAddressUtils';
import { fetchCoordinatesBatch } from '@/services/geocodeService';
import { setSelectedOrder } from '@/store/ducks/schedule';
import { getFilteredInspectors, getScheduleDate } from '@/store/selectors/schedule';

const initialFilters = {
  selectedInspectorIds: ['all'],
  scheduleType: 'all',
};

const initialLayers = {
  realEstates: true,
  scheduledOrders: true,
  inspectorBases: true,
  keyPickups: true,
  keyReturns: true,
  waitingOrders: true,
};

const hasConnectedDevice = inspector => inspector?.status !== 'DEVICE_NOT_CONNECTED';

const ScheduleMapContainer = ({ isVisible, onMapStateChange }) => {
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(initialFilters);
  const [layers, setLayers] = useState(initialLayers);
  const [coordinatesById, setCoordinatesById] = useState({});
  const [coordinatesLoading, setCoordinatesLoading] = useState(false);
  const [coordinatesResolved, setCoordinatesResolved] = useState(false);
  const lastLoadedCoordinatesKeyRef = useRef('');
  const [selectedMapOrderId, setSelectedMapOrderId] = useState(null);
  const [requestedMapOrder, setRequestedMapOrder] = useState(null);
  const [mapFocusRequest, setMapFocusRequest] = useState(null);

  const rawDate = useSelector(getScheduleDate);
  const scheduleDate = useMemo(() => {
    try {
      return rawDate ? format(new Date(rawDate), 'yyyy-MM-dd') : null;
    } catch {
      return null;
    }
  }, [rawDate]);

  const inspectors = useSelector(getFilteredInspectors);
  const scheduledOrders = useSelector(state => state.schedule.orders);
  const pendingOrders = useSelector(state => state.schedule.filteredPendingOrders);

  const activeInspectors = useMemo(
    () => inspectors.filter(inspector => !inspector.wasNotFiltered && hasConnectedDevice(inspector)),
    [inspectors]
  );

  const inspectorOptions = useMemo(
    () =>
      activeInspectors
        .map(inspector => ({
          value: String(inspector.public_id),
          label: inspector.name,
          document: inspector.document || inspector.cpf || '',
          initials: getInitials(inspector.name),
          color: getInspectorColor(inspector.public_id, activeInspectors),
        }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [activeInspectors]
  );

  const selectableInspectorIds = useMemo(
    () => inspectorOptions.map(option => option.value),
    [inspectorOptions]
  );

  const filteredScheduledOrders = useMemo(
    () =>
      scheduledOrders.filter(item => {
        const inspectorId = String(item.schedule?.who?.inspector_id || '');
        const isAllSelected = isAllInspectorsSelected(filters.selectedInspectorIds, inspectorOptions);
        const inspectorMatches = isAllSelected
          ? selectableInspectorIds.includes(inspectorId)
          : filters.selectedInspectorIds.includes(inspectorId);

        const scheduleTypeMatches =
          filters.scheduleType === 'all' ||
          (filters.scheduleType === 'flexible' && item.schedule?.flextime === true) ||
          (filters.scheduleType === 'fixed' && item.schedule?.flextime !== true);

        return inspectorMatches && scheduleTypeMatches;
      }),
    [filters, scheduledOrders, inspectorOptions, selectableInspectorIds]
  );

  const displayedGeocodableItems = useMemo(() => {
    const items = [];

    if (layers.scheduledOrders) {
      items.push(...filteredScheduledOrders);
    }
    if (layers.waitingOrders) {
      items.push(...pendingOrders);
    }
    if (layers.inspectorBases) {
      items.push(...activeInspectors);
    }

    return items;
  }, [layers.scheduledOrders, layers.waitingOrders, layers.inspectorBases, filteredScheduledOrders, pendingOrders, activeInspectors]);

  const displayedGeocodableItemsKey = useMemo(
    () =>
      [...new Set(displayedGeocodableItems.map(item => String(getGeocodableEntityId(item) || '')).filter(Boolean))]
        .sort()
        .join(','),
    [displayedGeocodableItems]
  );

  useEffect(() => {
    if (!isVisible) return undefined;
    if (coordinatesResolved && lastLoadedCoordinatesKeyRef.current === displayedGeocodableItemsKey) {
      return undefined;
    }

    let isCurrentRequest = true;

    const loadCoordinates = async () => {
      setCoordinatesLoading(true);

      const resolved = await fetchCoordinatesBatch(displayedGeocodableItems);

      if (isCurrentRequest) {
        setCoordinatesById(prev => ({ ...prev, ...resolved }));
        setCoordinatesLoading(false);
        setCoordinatesResolved(true);
        lastLoadedCoordinatesKeyRef.current = displayedGeocodableItemsKey;
      }
    };

    loadCoordinates();

    return () => {
      isCurrentRequest = false;
    };
  }, [coordinatesResolved, displayedGeocodableItemsKey, isVisible]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(current => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const handleInspectorToggle = useCallback(
    inspectorIdStr => {
      setFilters(current => {
        const allIds = inspectorOptions.map(opt => opt.value);
        const currentIds = current.selectedInspectorIds;
        const isAllCurrentlySelected = isAllInspectorsSelected(currentIds, allIds);

        let nextIds;

        if (isAllCurrentlySelected) {
          nextIds = allIds.filter(id => id !== inspectorIdStr);
        } else if (currentIds.includes(inspectorIdStr)) {
          nextIds = currentIds.filter(id => id !== inspectorIdStr);
        } else {
          nextIds = [...currentIds, inspectorIdStr];
        }

        if (nextIds.length === allIds.length) {
          nextIds = ['all'];
        }

        return {
          ...current,
          selectedInspectorIds: nextIds,
        };
      });
    },
    [inspectorOptions]
  );

  const handleSelectAllInspectors = useCallback(checked => {
    setFilters(current => ({
      ...current,
      selectedInspectorIds: checked ? ['all'] : [],
    }));
  }, []);

  const handleLayerToggle = useCallback(layer => {
    setLayers(currentLayers => ({
      ...currentLayers,
      [layer]: !currentLayers[layer],
    }));
  }, []);

  const handlePendingOrderClick = useCallback(order => {
    const orderId = getGeocodableEntityId(order);

    if (!orderId) return;

    const normalizedOrderId = String(orderId);

    setSelectedMapOrderId(normalizedOrderId);
    setRequestedMapOrder({ orderId: normalizedOrderId });
  }, []);

  const handleOrderMarkerClick = useCallback(
    orderId => {
      dispatch(setSelectedOrder({ orderId }));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!coordinatesResolved || !requestedMapOrder) return;

    const position = getScheduleMapPosition(coordinatesById, requestedMapOrder.orderId);

    if (position) {
      setMapFocusRequest({
        orderId: requestedMapOrder.orderId,
        position,
      });
    }
  }, [coordinatesById, coordinatesResolved, requestedMapOrder]);

  const unlocatedOrderIds = useMemo(() => {
    if (!coordinatesResolved) return [];

    return pendingOrders
      .map(order => getGeocodableEntityId(order))
      .filter(orderId => orderId && !getScheduleMapPosition(coordinatesById, orderId))
      .map(String);
  }, [coordinatesById, coordinatesResolved, pendingOrders]);

  const intl = useIntl();

  const { markers, polylines } = useMemo(
    () =>
      buildScheduleMapMarkers({
        coordinatesById,
        scheduledOrders: filteredScheduledOrders,
        pendingOrders,
        inspectors: activeInspectors,
        layers,
        selectedInspectorIds: filters.selectedInspectorIds,
        intl,
      }),
    [activeInspectors, coordinatesById, filteredScheduledOrders, filters.selectedInspectorIds, layers, pendingOrders, intl]
  );

  useEffect(() => {
    if (onMapStateChange) {
      onMapStateChange({
        selectedMapOrderId,
        unlocatedOrderIds,
        onPendingOrderClick: handlePendingOrderClick,
      });
    }
  }, [selectedMapOrderId, unlocatedOrderIds, handlePendingOrderClick, onMapStateChange]);

  return (
    <ScheduleMapComponent
      filters={filters}
      layers={layers}
      inspectorOptions={inspectorOptions}
      markers={markers}
      polylines={polylines}
      coordinatesLoading={coordinatesLoading}
      isVisible={isVisible}
      scheduleDate={scheduleDate}
      mapFocusRequest={mapFocusRequest}
      selectedMapOrderId={selectedMapOrderId}
      onFilterChange={handleFilterChange}
      onLayerToggle={handleLayerToggle}
      onInspectorToggle={handleInspectorToggle}
      onSelectAllInspectors={handleSelectAllInspectors}
      onOrderMarkerClick={handleOrderMarkerClick}
    />
  );
};

ScheduleMapContainer.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onMapStateChange: PropTypes.func,
};

ScheduleMapContainer.defaultProps = {
  onMapStateChange: null,
};

export default React.memo(ScheduleMapContainer);
